import { store } from 'redux/store';

import { generateEmail } from 'utils/filters';
import normalizeCif from 'utils/normalizeCif';

import { addPartners } from 'modules/_shared/redux/modalsActions';
import { createPartnerCategory } from 'modules/partners/redux/partnerCategoryActions';

import {
  ConstitutionExcelPreview,
  ConstitutionExcelRow,
} from '../types/ConstitutionExcel';
import { ConstitutionExistingPartner } from './fetchExistingPartnersForConstitution';
import { HoldingClassForConstitutionExcel } from './initConstitutionNumerations';

type Society = {
  _id: string;
  name: string;
  currentPartner?: number;
};

type User = {
  _id: string;
  email: string;
};

type PartnerCategory = {
  _id: string;
  name: string;
};

type ResolveOrCreatePartnersParams = {
  rows: ConstitutionExcelRow[];
  preview: ConstitutionExcelPreview;
  society: Society;
  user: User;
  operationDate: Date;
  holdingClasses: HoldingClassForConstitutionExcel[];
  partnerCategories: PartnerCategory[];
  existingPartners: ConstitutionExistingPartner[];
  newPartnerCategories: string[];
};

const uploadNewCategories = async (
  categories: string[],
  societyId: string
): Promise<Record<string, string>> => {
  if (!categories.length) return {};

  const newCategories = categories.map((category) => ({
    name: category,
    description: '',
    society: societyId,
  }));

  const uploadPromises = newCategories.map((category) =>
    store.dispatch(createPartnerCategory(category, false))
  );

  const uploadedCategories = await Promise.all(uploadPromises);

  return uploadedCategories.reduce<Record<string, string>>((acc, curr) => {
    if (curr?.name && curr?._id) {
      acc[curr.name] = curr._id;
    }
    return acc;
  }, {});
};

const getPartnerCategoryId = (
  row: ConstitutionExcelRow,
  partnerCategories: PartnerCategory[],
  createdCategories: Record<string, string>
): string | null => {
  const categoryName = row.investorType;

  if (!categoryName) return null;

  const existingCategory = partnerCategories.find(
    (category) => category.name?.toUpperCase() === categoryName.toUpperCase()
  );

  if (existingCategory) return existingCategory._id;

  return createdCategories[categoryName] || null;
};

export const resolveOrCreatePartnersFromExcel = async ({
  rows,
  preview,
  society,
  user,
  operationDate,
  holdingClasses,
  partnerCategories,
  existingPartners,
  newPartnerCategories,
}: ResolveOrCreatePartnersParams): Promise<Record<string, string>> => {
  const partnerIdByCif: Record<string, string> = {};

  existingPartners.forEach((partner) => {
    if (partner.cif) {
      partnerIdByCif[partner.cif] = partner._id;
    }
  });

  const createdCategories = await uploadNewCategories(
    newPartnerCategories,
    society._id
  );

  const uniqueCifs = [...new Set(rows.map((row) => row.cif))];
  const uniqueRows = uniqueCifs.map((cif) => rows.find((row) => row.cif === cif)!);

  const rowsToCreate = uniqueRows.filter((row) => !partnerIdByCif[row.cif]);

  if (rowsToCreate.length) {
    const defaultHoldingClassId = holdingClasses?.[0]?._id || '';

    const formattedPartners = rowsToCreate.map((row, index) => ({
      society: society._id,
      date: operationDate,
      user: user._id,
      societyName: society.name,
      userEmail: user.email,
      source: 'UPLOAD',
      email:
        row.email ||
        generateEmail(society, (society.currentPartner || 0) + index + 1),
      cif: row.cif,
      idType: row.idType,
      name: row.name,
      category: getPartnerCategoryId(row, partnerCategories, createdCategories),
      birthdate: row.birthdate,
      civilStatus: row.civilStatus,
      shares: row.shares,
      nationality: row.nationality,
      address: row.address,
      shareClass: row.holdingClassName || defaultHoldingClassId,
      employeeNumber: row.employeeNumber,
      bankAccountNumber: row.bankAccountNumber,
      taxAddress: row.taxAddress,
      isNaturalPerson: row.isNaturalPerson,
    }));

    const uploadedPartners = await store.dispatch(addPartners(formattedPartners));

    uploadedPartners?.forEach((partner: { _id: string; cif?: string }) => {
      const cif = normalizeCif(partner?.cif || '');
      if (cif) {
        partnerIdByCif[cif] = partner._id;
      }
    });
  }

  const uniquePreviewCifs = [...new Set(preview.items.map((item) => item.cif))];
  const missingCifs = uniquePreviewCifs.filter((cif) => !partnerIdByCif[cif]);

  if (missingCifs.length) {
    throw new Error(`Missing partner IDs for CIFs: ${missingCifs.join(', ')}`);
  }

  return partnerIdByCif;
};
